import { ReactElement } from 'react';
import { combineLatest, map } from 'rxjs';
import styled from 'styled-components';
import { connect } from '@ardoq/rxbeach';
import { APICurrentUser } from '@ardoq/api-types';
import { activeScenarioOperations, userOperations } from '@ardoq/core';
import type { ScenarioModeState } from '@ardoq/data-model';
import { currentDate, parseDate } from '@ardoq/date-time';
import {
  type FeatureContext,
  featureOperations,
  Features,
} from '@ardoq/features';
import { NO_EXPORT_CLASS_NAME } from '@ardoq/global-consts';
import { t, type I18nLocale } from '@ardoq/i18n';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { getFocusableElement } from '@ardoq/hooks';
import { Box } from '@ardoq/layout';
import { ExternalLink } from '@ardoq/link';
import { ClosableNotification, InfoNotification } from '@ardoq/status-ui';
import { isPresentationMode } from 'env';
import {
  DontShowAgain,
  setDontShowAgainSetting,
} from 'models/dontShowAgainSettings';
import { focusedChrome$ } from 'scope/focusedChrome$';
import {
  type FocusedChrome,
  focusedChromeOperations,
} from 'scope/focusedChromeOperations';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { loadedCurrentUser$ } from 'streams/currentUser/currentUser$';
import { featureContext$ } from 'streams/featureContext$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { isViewpointMode$ } from 'traversals/loadedGraph$';

const SHOW_FROM_DATE = parseDate('2026-10-27');
const SHOW_UNTIL_DATE = parseDate('2026-11-16');

const BannerContainer = styled(Box)`
  @media print {
    display: none;
  }
`;

type SlidesEndAnnouncementProps = {
  isVisible: boolean;
  i18nLocale: I18nLocale;
};

const focusSettingsBarAbove = (): void => {
  const settingsBar = document.activeElement?.closest(
    `${BannerContainer}`
  )?.previousElementSibling;
  if (settingsBar instanceof HTMLElement) {
    getFocusableElement(settingsBar).focus();
  }
};

const dismissBanner = (): void => {
  focusSettingsBarAbove();
  setDontShowAgainSetting(DontShowAgain.WORKSPACE_MODE_SLIDES_END_INFO, true);
};

const LearnMoreLink = ({
  i18nLocale,
}: {
  i18nLocale: I18nLocale;
}): ReactElement => (
  <ExternalLink
    href={KnowledgeBaseLink.VIEWS_AVAILABILITY_AND_TRANSITION_TIMELINE}
    opensInNewTabLabel={t(
      i18nLocale,
      'appLayout:workspaceModeSlidesEndBanner.opensInNewTab',
      '(opens in new tab)'
    )}
  >
    {t(
      i18nLocale,
      'appLayout:workspaceModeSlidesEndBanner.learnMore',
      "Learn more about what's changing"
    )}
  </ExternalLink>
);

const WorkspaceModeSlidesEndBannerView = ({
  isVisible,
  i18nLocale,
}: SlidesEndAnnouncementProps): ReactElement | null => {
  if (!isVisible) {
    return null;
  }

  return (
    <BannerContainer
      className={`${NO_EXPORT_CLASS_NAME} ardoq-announcement-banner`}
      paddingX="medium"
      paddingY="small"
    >
      <ClosableNotification
        mode="closeOnly"
        status="info"
        dismissNotification={dismissBanner}
        closeButtonLabel={t(i18nLocale, 'common:close', 'Close')}
      >
        {t(
          i18nLocale,
          'appLayout:workspaceModeSlidesEndBanner.description',
          "From December 16, 2026, you won't be able to add new slides to Presentations in workspace mode. Your existing slides stay available."
        )}{' '}
        <LearnMoreLink i18nLocale={i18nLocale} />
      </ClosableNotification>
    </BannerContainer>
  );
};

const WorkspaceModeSlidesEndNoteView = ({
  isVisible,
  i18nLocale,
}: SlidesEndAnnouncementProps): ReactElement | null => {
  if (!isVisible) {
    return null;
  }

  return (
    <InfoNotification alignItems="center">
      {t(
        i18nLocale,
        'appLayout:workspaceModeSlidesEndNote.description',
        'You can add new slides in workspace mode until December 16, 2026. Your existing slides stay available.'
      )}{' '}
      <LearnMoreLink i18nLocale={i18nLocale} />
    </InfoNotification>
  );
};

const isWithinShowWindow = (now: Date): boolean =>
  now >= SHOW_FROM_DATE && now < SHOW_UNTIL_DATE;

const isAnnouncementActiveNow = (): boolean =>
  !isPresentationMode() && isWithinShowWindow(currentDate());

const isAnnouncementShownTo = (
  currentUser: APICurrentUser,
  featureContext: FeatureContext,
  activeScenario: ScenarioModeState
): boolean =>
  !activeScenarioOperations.isInScenarioMode(activeScenario) &&
  featureOperations.hasFeature(
    featureContext,
    Features.WORKSPACE_MODE_SLIDES_END_ANNOUNCEMENT
  ) &&
  userOperations.hasWriterRolePlus(currentUser);

const toWorkspaceModeSlidesEndBannerProps = (
  currentUser: APICurrentUser,
  focusedChrome: FocusedChrome,
  featureContext: FeatureContext,
  activeScenario: ScenarioModeState,
  i18nLocale: I18nLocale,
  isAnnouncementActive: boolean
): SlidesEndAnnouncementProps => ({
  isVisible:
    isAnnouncementActive &&
    isAnnouncementShownTo(currentUser, featureContext, activeScenario) &&
    !currentUser.settings[DontShowAgain.WORKSPACE_MODE_SLIDES_END_INFO] &&
    focusedChromeOperations.allowsAddToPresentation(focusedChrome),
  i18nLocale,
});

export const WorkspaceModeSlidesEndBanner = connect(
  WorkspaceModeSlidesEndBannerView,
  combineLatest([
    loadedCurrentUser$,
    focusedChrome$,
    featureContext$,
    activeScenario$,
    i18nLocale$,
  ]).pipe(
    map(
      ([
        currentUser,
        focusedChrome,
        featureContext,
        activeScenario,
        i18nLocale,
      ]) =>
        toWorkspaceModeSlidesEndBannerProps(
          currentUser,
          focusedChrome,
          featureContext,
          activeScenario,
          i18nLocale,
          isAnnouncementActiveNow()
        )
    )
  )
);

const toWorkspaceModeSlidesEndNoteProps = (
  currentUser: APICurrentUser,
  featureContext: FeatureContext,
  activeScenario: ScenarioModeState,
  { isViewpointMode }: { isViewpointMode: boolean },
  i18nLocale: I18nLocale,
  isAnnouncementActive: boolean
): SlidesEndAnnouncementProps => ({
  isVisible:
    isAnnouncementActive &&
    !isViewpointMode &&
    isAnnouncementShownTo(currentUser, featureContext, activeScenario),
  i18nLocale,
});

export const WorkspaceModeSlidesEndNote = connect(
  WorkspaceModeSlidesEndNoteView,
  combineLatest([
    loadedCurrentUser$,
    featureContext$,
    activeScenario$,
    isViewpointMode$,
    i18nLocale$,
  ]).pipe(
    map(
      ([
        currentUser,
        featureContext,
        activeScenario,
        viewpointMode,
        i18nLocale,
      ]) =>
        toWorkspaceModeSlidesEndNoteProps(
          currentUser,
          featureContext,
          activeScenario,
          viewpointMode,
          i18nLocale,
          isAnnouncementActiveNow()
        )
    )
  )
);
